import {
  apiUrl,
  getList,
  deleteRecord,
  getSingleRecord,
  saveRecord,
  getStatsData,
  getAll,
  postArray,
  deleteArray,
  upload,
  get,
  getAuditData,
  postAction,
} from './apiUtils';
import type { ApiListParamsWithUnderscores } from '../types/api/listParams';
import type { ListApiResponse } from '../types/apiResponse';
import type { Stats } from '../types/stats';
import type {
  GameSkuViewModel,
  SkuSaveModel,
  CompanyKeyStockViewModel,
  KeyCodeKeyPoolModel,
} from '../types/api';

const baseUrl = apiUrl + '/gameskus/';

export function getSkus(
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<GameSkuViewModel>> {
  return getList(`${baseUrl}search`, params);
}

export function getAllSKUs(): Promise<GameSkuViewModel[]> {
  return getAll(`${baseUrl}search`);
}

export function getSkuById(id: string): Promise<GameSkuViewModel> {
  return getSingleRecord(baseUrl, id);
}

/** Save payload: singleKeyValue can be string (form) or number */
export function saveSku(
  gameid: string,
  sku: Omit<SkuSaveModel, 'singleKeyValue'> & {
    singleKeyValue?: string | number;
    [key: string]: unknown;
  }
): Promise<GameSkuViewModel> {
  const raw = sku.singleKeyValue;
  const singleKeyValue =
    typeof raw === 'string'
      ? parseFloat(raw.replace(/,/g, '')) || 0
      : Number(raw) ?? 0;
  const payload: SkuSaveModel = { ...sku, singleKeyValue };
  return saveRecord(`${apiUrl}/games/${gameid}/sku/`, payload);
}

export function deleteSku(id: string): Promise<{ id: string }> {
  return deleteRecord(baseUrl, id);
}

export function getStats(id: string): Promise<Stats> {
  return getStatsData(baseUrl, id);
}

export function getStatsByDay(id: string): Promise<Stats[]> {
  return get(`${baseUrl}${id}/statsByDay/`);
}

export function getAggregateStats(
  id: string,
  from: Date,
  to: Date
): Promise<Stats> {
  const fromStr = from.toISOString().split('T')[0];
  const toStr = to.toISOString().split('T')[0];
  return get(
    `${baseUrl}${id}/aggregateStats?fromDate=${fromStr}&toDate=${toStr}`
  );
}

export function getAudit(id: string): Promise<unknown> {
  return getAuditData(baseUrl, id);
}

export function addAudit(id: string, comment: string): Promise<unknown> {
  return postAction(`${baseUrl}${id}/audit`, { comment });
}

export function addTerritories(
  id: string,
  gameid: string,
  territories: string[]
): Promise<GameSkuViewModel> {
  return postArray(
    `${apiUrl}/games/${gameid}/sku/${id}/territories`,
    territories
  );
}

export function removeTerritories(
  id: string,
  gameid: string,
  territories: string[]
): Promise<GameSkuViewModel> {
  return deleteArray(
    `${apiUrl}/games/${gameid}/sku/${id}/territories`,
    territories
  );
}

export function addWorkflows(
  id: string,
  gameid: string,
  workflows: string[]
): Promise<GameSkuViewModel> {
  return postArray(
    `${apiUrl}/games/${gameid}/sku/${id}/keyWorkflows`,
    workflows
  );
}

export function removeWorkflows(
  id: string,
  gameid: string,
  workflows: string[]
): Promise<GameSkuViewModel> {
  return deleteArray(
    `${apiUrl}/games/${gameid}/sku/${id}/keyWorkflows`,
    workflows
  );
}

export function uploadToKeyPool(
  id: string,
  gameid: string,
  keyWorkflowId: string,
  record: FormData | Record<string, unknown>
): Promise<unknown> {
  return upload(
    `${apiUrl}/games/${gameid}/sku/${id}/keyWorkflow/${keyWorkflowId}/upload`,
    record
  );
}

export function validateCodeFile(
  id: string,
  gameid: string,
  file: File | Blob | FormData
): Promise<unknown> {
  return upload(
    `${apiUrl}/games/${gameid}/sku/${id}/upload/validate`,
    file
  );
}

export function getKeyPools(
  id: string,
  gameId: string,
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<KeyCodeKeyPoolModel>> {
  return getList(`${apiUrl}/games/${gameId}/sku/${id}/keyPools`, params);
}

export function getSkuCompanyStocks(
  id: string
): Promise<ListApiResponse<CompanyKeyStockViewModel>> {
  return get(`${baseUrl}${id}/companyStocks`);
}

export function uploadPricing(
  record: FormData | Record<string, unknown>
): Promise<unknown> {
  return upload(`${baseUrl}upload`, record);
}
